import React, { Fragment, useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Row } from "./row";
import { Box, Button } from "@flexisaf/flexibull2";
import { v4 as uuidv4 } from "uuid";

import { SchemaContext } from "./container";

export const SectionWrapper = styled("div").attrs({
  className: "flex-render-section",
})`
  margin: 0 0 1.5rem;
  padding: 1.35rem 1.5rem 1rem;
  background: #fff;
  border: 1px solid #ebe7f3;
  border-radius: 16px;
  box-shadow: 0 10px 28px rgba(47, 42, 58, 0.05);

  ${(props) =>
    props.styler === "sectioned" &&
    css`
      box-shadow: 0 8px 24px rgba(61, 43, 86, 0.06);
      border-radius: 16px;
      padding: 0;
      overflow: hidden;

      & .flex-render-section-header {
        background: #fff;
        padding: 1.25rem 1.5rem;
        border-radius: 0;
        border-bottom: 1px solid #ebe7f3;
        & h3 {
          margin: 0 0 0.35rem 0;
          padding: 0 0 0 0.75rem;
          border-left: 3px solid ${(props) => props.theme?.PrimaryColor || "#825efb"};
          font-size: 1.2rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: #2f2a3a;
        }
        & p {
          margin: 0 0 0 0.75rem;
          padding: 0;
          font-size: 0.9rem;
          line-height: 1.5;
          color: #5c5668;
        }
      }
      & .flex-render-section-body {
        padding: 1.25rem 1.5rem 1.5rem;
        border-radius: 0;
      }
    `}

  ${(props) =>
    props.styler === "wizard" &&
    css`
      & .flex-render-section-header {
        border-bottom: 1px solid #ebe7f3;
        padding-bottom: 0.9rem;
        margin-bottom: 0.25rem;
        & h3 {
          margin: 0 0 0.35rem 0;
          padding: 0 0 0 0.75rem;
          border-left: 3px solid ${(props) => props.theme?.PrimaryColor || "#825efb"};
          font-size: 1.25rem;
          font-weight: 700;
          letter-spacing: -0.015em;
          text-transform: none;
          color: #2f2a3a;
          font-family: "Inter", system-ui, sans-serif;
        }
        & p {
          margin: 0 0 0 0.75rem;
          padding: 0;
          font-size: 0.9rem;
          line-height: 1.55;
          color: #5c5668;
          max-width: 54rem;
        }
      }
      & .flex-render-section-body {
        padding: 0.5rem 0 0;
      }

      @media (max-width: 800px) {
        padding: 1.05rem 0.95rem 0.85rem;
        border-radius: 12px;

        .flex-render-section-body {
          padding-top: 0.25rem;
        }

        & .flex-render-section-header h3 {
          font-size: 1.1rem;
        }
      }
    `}
`;

const SectionNavigationBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;
  margin: 0.85rem 0 0 !important;
  padding-top: 0.9rem;
  border-top: 1px solid #ebe7f3;

  & .flexi-button {
    border-radius: 9px !important;
    font-weight: 600 !important;
    min-width: 112px;
  }

  @media (max-width: 600px) {
    & .flexi-button {
      font-size: 14px;
      width: 90px !important;
      min-width: 90px;
    }
  }
`;

export const Section = (props) => {
  const { styler, duplicateFields, setDuplicateFields } =
    useContext(SchemaContext);
  const [rowSchemas, setRowSchemas] = useState([]);
  let {
    title,
    description,
    rows,
    id: sectionId,
    canDuplicate,
    addSectionAlias,
    maxDuplicate,
  } = props;
  useEffect(() => {
    const rrs = !Array.isArray(rows) ? [rows] : rows;
    const duplicateIds = duplicateFields?.sections?.[sectionId] || [];
    const newRows = [
      { id: sectionId, rows: rrs },
      ...duplicateIds.map((id) => ({ id, rows: [...rrs] })),
    ];

    setRowSchemas(newRows);
  }, [duplicateFields, rows]);

  const handleAddEntry = () => {
    const duplicateFieldsCopy = { ...duplicateFields };
    duplicateFieldsCopy.sections[sectionId] = [
      ...(duplicateFieldsCopy?.sections?.[sectionId] || []),
      uuidv4(),
    ];
    setDuplicateFields(duplicateFieldsCopy);
  };

  const deleteEntrySection = (r) => {
    const duplicateFieldsCopy = { ...duplicateFields };
    duplicateFieldsCopy.sections[sectionId] = (
      duplicateFieldsCopy?.sections?.[sectionId] || []
    ).filter((id) => id !== r.id);
    setDuplicateFields(duplicateFieldsCopy);
  };

  const renderNavigation = () => (
    <SectionNavigationBox align="right">
      {props.onPrevious && (
        <Button
          iconLeft={<i className="flexibull-left" />}
          width={120}
          height={44}
          onClick={props.onPrevious}
          spaceRight
          pale
        >
          Previous
        </Button>
      )}
      {props.onNext && (
        <Button
          iconRight={<i className="flexibull-right" />}
          width={120}
          height={44}
          onClick={props.onNext}
        >
          Next
        </Button>
      )}
      {props.onSubmit && (
        <Button
          iconRight={<i className="flexibull-right" />}
          height={44}
          onClick={props.onSubmit}
          progress={props.saving}
        >
          {props.submitTitle || "Submit"}
        </Button>
      )}
    </SectionNavigationBox>
  );

  return (
    <SectionWrapper styler={styler}>
      <div className="flex-render-section-header">
        {title && <h3>{title}</h3>}
        {description && (
          <p dangerouslySetInnerHTML={{ __html: description }} />
        )}
      </div>
      {rowSchemas.map((r, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <Box>
              <Box align="right">
                <Button
                  pale
                  height={30}
                  onClick={() => deleteEntrySection(r)}
                  color="#EE1B87"
                  fontColor="#EE1B87"
                  round
                  font="16px"
                  icon={<i className="flexibull-cancel"></i>}
                />
              </Box>
              <Box>{title && <h3>{`${title} (${index + 1})`}</h3>}</Box>
            </Box>
          )}
          <div key={index} className="flex-render-section-body">
            {r &&
              r.rows.map((row, j) => (
                <Row
                  key={j}
                  duplicateNumber={r.id}
                  sectionId={sectionId}
                  {...row}
                />
              ))}
          </div>
        </Fragment>
      ))}
      {canDuplicate &&
        rowSchemas.length < (+maxDuplicate > 0 ? maxDuplicate : Infinity) && (
          <Box align="right">
            <Button
              height={38}
              onClick={handleAddEntry}
              spaceRight
              pale
              iconRight={<i className="flexibull-plus" />}
            >
              {addSectionAlias || "Add Entry"}
            </Button>
          </Box>
        )}
      {(props.onPrevious || props.onNext || props.onSubmit) &&
        renderNavigation()}
    </SectionWrapper>
  );
};
